import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, errText, media } from "../../lib/api";
import { CheckCircle2, Upload, AlertTriangle, Loader2 } from "lucide-react";
import { MOROCCO } from "../../lib/countries";
import LocationPicker from "../../components/LocationPicker";
import PartnerLanding from "./PartnerLanding";
import { trackPartnerSignup } from "../../components/MarketingLoader";

// Full form-copy dictionary. `T` below is picked from this map based on the URL locale.
const COPY = {
  fr: {
    title: "Devenir fleuriste partenaire Fleurinelle",
    intro: "Nous travaillons avec des boutiques établies à Casablanca, Marrakech, Rabat, Tanger et Agadir, disposant de leur propre capacité de livraison. Les commandes vous arrivent déjà payées, le règlement est hebdomadaire en dirhams, et vous n'avez aucun service client à gérer.",
    moroccoOnly: "🇲🇦 Fleurinelle ne travaille qu'avec des boutiques établies au Maroc.",
    latinNotice: "Toutes les informations doivent être saisies en caractères latins (a-z, 0-9).",
    shop: "Nom de la boutique", owner: "Nom du gérant", email: "E-mail professionnel", password: "Mot de passe (8 caractères min)",
    phone: "Téléphone du gérant", whatsapp: "WhatsApp", city: "Ville", address: "Adresse complète de la boutique",
    ice: "Numéro ICE", years: "Années d'activité", zones: "Zones que vous couvrez",
    capacity: "Commandes possibles par jour", website: "Site web", instagram: "Instagram",
    notes: "Autre chose à nous dire", submit: "Envoyer la candidature",
    submitting: "Envoi en cours…", success: "Candidature envoyée",
    successHint: "Chargez vos documents depuis votre espace pour accélérer la vérification.",
    section1: "1. Votre boutique", section1Hint: "Ces informations apparaissent sur votre profil interne. Rien n'est publié tel quel côté client.",
    section2: "2. Vos identifiants de connexion", section2Hint: "C'est avec ceci que vous vous connecterez au tableau de bord fleuriste.",
    section3: "3. Livraison", section3Hint: "Fleurinelle fixe les frais de livraison. Vous choisissez qui livre.",
    modeNetwork: "Réseau Fleurinelle", modeNetworkDesc: "Nos livreurs s'occupent de tout. Les frais reviennent à la plateforme.",
    modeSelf: "Ma propre équipe", modeSelfDesc: "Vos livreurs livrent. Vous gardez 100 % des frais de livraison et fixez vos propres prix par zone.",
    section4: "3. Coordonnées bancaires", section4Hint: "Règlement hebdomadaire en dirhams. Le RIB reste privé et n'apparaît jamais sur le site.",
    bank: "Banque", holder: "Titulaire du compte", holderHint: "Nom exactement tel qu'il figure sur le RIB.",
    rib: "RIB (24 chiffres)", ribHint: "Après l'envoi, chargez l'attestation de RIB de la banque. Sans elle, l'activation reste bloquée.",
    docsSection: "4. Documents obligatoires",
    docsSectionHint: "Chargez chaque document en JPG ou PDF (8 Mo max). Nos équipes les vérifient sous 24 h ouvrées.",
    docsAccept: "JPG ou PDF · 8 Mo max",
    docUpload: "Charger", docChange: "Remplacer", recto: "Recto", verso: "Verso",
    docIce: "Attestation ICE (DGI)",
    docRc: "Registre de commerce (moins de 3 mois)",
    docCin: "CIN du gérant (recto-verso)",
    docRib: "Attestation de RIB de la banque",
    section5: "5. Un mot pour nous (optionnel)",
    notesHint: "Photos préférées, spécialités, saison forte : tout ce qui aide notre équipe à vous connaître.",
    consent: "J'ai lu et j'accepte le", agreement: "Contrat de fleuriste partenaire", and: "et l'", addendum: "Addendum de traitement des données", consentEnd: ", au nom de ma boutique.",
    submitHint: "Cela prend moins de 5 minutes. Nous répondons sous 24 h ouvrées.",
    iceHint: "15 chiffres, sur votre attestation d'ICE (DGI). Facturation uniquement.",
    addressHint: "N° et rue, quartier, code postal.",
    pickupLabel: "Point de retrait sur la carte", pickupHint: "Recherchez votre boutique sur Google Maps ou déposez la punaise directement à l'entrée. Les livreurs partenaires suivront ce point exact.",
    photoLabel: "Photo de la devanture", photoHint: "Une photo de l'entrée de votre boutique. Les livreurs la verront au retrait pour reconnaître la bonne porte.",
    photoUpload: "Charger une photo", photoChange: "Changer la photo", photoBusy: "Chargement…",
    zonesHint: "Sélectionnez toutes les zones que vous êtes prêts à livrer.",
    optional: "Optionnel",
    yearsPh: "ex. 5", capacityPh: "ex. 8", websitePh: "https://maisonnour.ma", instagramPh: "@fleurinelle",
    shopPh: "Maison Nour", ownerPh: "Prénom et nom", emailPh: "contact@maisonnour.ma", passwordPh: "••••••••",
    phonePh: "6 12 34 56 78", icePh: "ex. 002345678000012", addressPh: "12 rue Bir Anzarane, Racine, Casablanca 20100",
    bankPh: "Attijariwafa, BMCE, Banque Populaire…", holderPh: "Ex. SARL Maison Nour", ribPh: "007810000123456789012345",
    notesPh: "Ex. Nous préparons beaucoup de mariages en été…",
    whatsappHint: "Optionnel. Laisser vide = identique au téléphone.",
    passwordHint: "8 caractères minimum.",
    chooseCity: "— Choisir —",
    errPhone: "Numéro trop court.", errIce: "chiffres — l'ICE marocain contient exactement 15 chiffres.",
    errRib: "chiffres — un RIB marocain contient exactement 24 chiffres.",
    errEmail: "Format d'e-mail invalide.", errPassword: "caractères minimum.",
    errWebsite: "URL invalide. Format attendu : https://votre-site.ma",
    errName: "Nom trop court.", errAddress: "Adresse trop courte.",
    status: "Votre candidature", documents: "Documents", upload: "Charger",
    reuploadTitle: "Documents à recharger", reuploadHint: "Voici uniquement les documents refusés ou manquants. Rechargez-les pour continuer.",
    viewAll: "Voir tous les documents", allUpToDate: "Tous vos documents sont à jour. Merci !",
    stageLabels: {
      applied: "Reçue", documents_requested: "Documents à compléter", documents_verified: "Documents validés",
      test_order: "Commande test", active: "Active", rejected: "Non retenue",
    },
  },
  en: {
    title: "Become a Fleurinelle Partner Florist",
    intro: "We work with established florists in Casablanca, Marrakech, Rabat, Tangier and Agadir with their own delivery capability. Orders arrive prepaid, payouts land weekly in dirhams, and you handle no customer service.",
    moroccoOnly: "🇲🇦 Fleurinelle only works with florists physically established in Morocco.",
    latinNotice: "All fields must be entered in Latin characters (a-z, 0-9).",
    shop: "Shop name", owner: "Owner's full name", email: "Business email", password: "Password (min 8 characters)",
    phone: "Owner's phone", whatsapp: "WhatsApp", city: "City", address: "Full shop address",
    ice: "ICE tax ID", years: "Years in business", zones: "Zones you cover",
    capacity: "Orders you can prepare per day", website: "Website", instagram: "Instagram",
    notes: "Anything else to tell us", submit: "Submit application",
    submitting: "Sending…", success: "Application submitted",
    successHint: "Upload your documents from your dashboard to speed up review.",
    section1: "1. Your shop", section1Hint: "This shows on your internal profile only. Nothing is published as-is to buyers.",
    section2: "2. Login details", section2Hint: "You'll sign in to the florist dashboard with these.",
    section3: "3. Delivery", section3Hint: "Fleurinelle sets delivery fees. You choose who delivers.",
    modeNetwork: "Fleurinelle network", modeNetworkDesc: "Our drivers handle everything. Fees go to the platform.",
    modeSelf: "My own team", modeSelfDesc: "Your drivers deliver. You keep 100 % of the delivery fee and set your own per-zone pricing.",
    section4: "3. Bank details", section4Hint: "Weekly payouts in dirhams. Your RIB stays private and is never shown on the site.",
    bank: "Bank", holder: "Account holder", holderHint: "Exactly as it appears on your RIB.",
    rib: "RIB (24 digits)", ribHint: "After submitting, upload the bank's RIB certificate. Activation stays blocked without it.",
    docsSection: "4. Mandatory documents",
    docsSectionHint: "Upload each document as JPG or PDF (8 MB max). Our team reviews within 24 business hours.",
    docsAccept: "JPG or PDF · 8 MB max",
    docUpload: "Upload", docChange: "Replace", recto: "Front", verso: "Back",
    docIce: "ICE certificate (DGI)",
    docRc: "Commercial registry (RC — less than 3 months)",
    docCin: "Owner's national ID (both sides)",
    docRib: "Bank RIB certificate",
    section5: "5. A word for us (optional)",
    notesHint: "Favourite photos, specialities, busy seasons — anything that helps our team know you.",
    consent: "I have read and accept the", agreement: "Partner Florist Agreement", and: "and the", addendum: "Data Processing Addendum", consentEnd: ", on behalf of my shop.",
    submitHint: "Under 5 minutes to apply. We get back to you within 24 business hours.",
    iceHint: "15 digits, from your ICE certificate (DGI). Used for invoicing only.",
    addressHint: "Number, street, district, postal code.",
    pickupLabel: "Pickup point on the map", pickupHint: "Search your shop on Google Maps or drop the pin exactly at your door. Partner drivers will follow this point.",
    photoLabel: "Shop-front photo", photoHint: "A photo of the entrance of your shop. Drivers will use it at pickup to recognise the right door.",
    photoUpload: "Upload a photo", photoChange: "Change photo", photoBusy: "Uploading…",
    zonesHint: "Select every zone you're willing to deliver to.",
    optional: "Optional",
    yearsPh: "e.g. 5", capacityPh: "e.g. 8", websitePh: "https://maisonnour.ma", instagramPh: "@fleurinelle",
    shopPh: "Maison Nour", ownerPh: "First and last name", emailPh: "contact@maisonnour.ma", passwordPh: "••••••••",
    phonePh: "6 12 34 56 78", icePh: "e.g. 002345678000012", addressPh: "12 rue Bir Anzarane, Racine, Casablanca 20100",
    bankPh: "Attijariwafa, BMCE, Banque Populaire…", holderPh: "e.g. SARL Maison Nour", ribPh: "007810000123456789012345",
    notesPh: "e.g. We prepare a lot of weddings in summer…",
    whatsappHint: "Optional. Leave empty = same as phone.",
    passwordHint: "8 characters minimum.",
    chooseCity: "— Choose —",
    errPhone: "Number too short.", errIce: "digits — a Moroccan ICE has exactly 15 digits.",
    errRib: "digits — a Moroccan RIB has exactly 24 digits.",
    errEmail: "Invalid email format.", errPassword: "characters minimum.",
    errWebsite: "Invalid URL. Expected format: https://your-site.ma",
    errName: "Name too short.", errAddress: "Address too short.",
    status: "Your application", documents: "Documents", upload: "Upload",
    reuploadTitle: "Documents to re-upload", reuploadHint: "These are only the documents that were rejected or are missing. Re-upload them to continue.",
    viewAll: "View all documents", allUpToDate: "All your documents are up to date. Thank you!",
    stageLabels: {
      applied: "Received", documents_requested: "Documents needed", documents_verified: "Documents verified",
      test_order: "Test order", active: "Active", rejected: "Not accepted",
    },
  },
  es: {
    title: "Sea socio florista Fleurinelle",
    intro: "Trabajamos con floristerías establecidas en Casablanca, Marrakech, Rabat, Tánger y Agadir con capacidad de entrega propia. Los pedidos llegan ya pagados, los pagos son semanales en dirhams y usted no gestiona la atención al cliente.",
    moroccoOnly: "🇲🇦 Fleurinelle solo trabaja con floristerías establecidas físicamente en Marruecos.",
    latinNotice: "Todos los campos deben rellenarse en caracteres latinos (a-z, 0-9).",
    shop: "Nombre de la tienda", owner: "Nombre del titular", email: "Correo profesional", password: "Contraseña (mín. 8 caracteres)",
    phone: "Teléfono del titular", whatsapp: "WhatsApp", city: "Ciudad", address: "Dirección completa de la tienda",
    ice: "Número ICE", years: "Años de actividad", zones: "Zonas que cubre",
    capacity: "Pedidos posibles por día", website: "Sitio web", instagram: "Instagram",
    notes: "Algo más que contarnos", submit: "Enviar candidatura",
    submitting: "Enviando…", success: "Candidatura enviada",
    successHint: "Suba sus documentos desde su panel para acelerar la revisión.",
    section1: "1. Su tienda", section1Hint: "Esta información solo aparece en su perfil interno. Nada se publica tal cual al comprador.",
    section2: "2. Datos de acceso", section2Hint: "Iniciará sesión en el panel del florista con estos datos.",
    section3: "3. Entrega", section3Hint: "Fleurinelle fija los gastos de envío. Usted elige quién entrega.",
    modeNetwork: "Red Fleurinelle", modeNetworkDesc: "Nuestros repartidores se encargan de todo. Los gastos van a la plataforma.",
    modeSelf: "Mi propio equipo", modeSelfDesc: "Sus repartidores entregan. Conserva el 100 % del gasto de envío y fija sus propios precios por zona.",
    section4: "3. Datos bancarios", section4Hint: "Pagos semanales en dirhams. Su RIB queda privado y nunca se muestra en el sitio.",
    bank: "Banco", holder: "Titular de la cuenta", holderHint: "Exactamente como aparece en el RIB.",
    rib: "RIB (24 dígitos)", ribHint: "Tras el envío, suba el certificado RIB del banco. Sin él, la activación queda bloqueada.",
    docsSection: "4. Documentos obligatorios",
    docsSectionHint: "Suba cada documento en JPG o PDF (máx. 8 MB). Nuestro equipo los revisa en 24 h laborables.",
    docsAccept: "JPG o PDF · máx. 8 MB",
    docUpload: "Subir", docChange: "Reemplazar", recto: "Anverso", verso: "Reverso",
    docIce: "Certificado ICE (DGI)",
    docRc: "Registro mercantil (RC — menos de 3 meses)",
    docCin: "DNI del titular (ambas caras)",
    docRib: "Certificado RIB del banco",
    section5: "5. Un mensaje para nosotros (opcional)",
    notesHint: "Fotos favoritas, especialidades, temporada alta: todo lo que ayude a nuestro equipo a conocerle.",
    consent: "He leído y acepto el", agreement: "Contrato de florista socio", and: "y el", addendum: "Anexo de tratamiento de datos", consentEnd: ", en nombre de mi tienda.",
    submitHint: "Menos de 5 minutos. Respondemos en 24 h laborables.",
    iceHint: "15 dígitos, del certificado ICE (DGI). Solo para facturación.",
    addressHint: "Número, calle, barrio, código postal.",
    pickupLabel: "Punto de recogida en el mapa", pickupHint: "Busque su tienda en Google Maps o coloque la chincheta exactamente en la puerta. Los repartidores seguirán este punto.",
    photoLabel: "Foto del escaparate", photoHint: "Una foto de la entrada de su tienda. Los repartidores la verán al recoger para reconocer la puerta correcta.",
    photoUpload: "Subir una foto", photoChange: "Cambiar foto", photoBusy: "Subiendo…",
    zonesHint: "Seleccione todas las zonas que está dispuesto a cubrir.",
    optional: "Opcional",
    yearsPh: "p. ej. 5", capacityPh: "p. ej. 8", websitePh: "https://maisonnour.ma", instagramPh: "@fleurinelle",
    shopPh: "Maison Nour", ownerPh: "Nombre y apellidos", emailPh: "contact@maisonnour.ma", passwordPh: "••••••••",
    phonePh: "6 12 34 56 78", icePh: "p. ej. 002345678000012", addressPh: "12 rue Bir Anzarane, Racine, Casablanca 20100",
    bankPh: "Attijariwafa, BMCE, Banque Populaire…", holderPh: "p. ej. SARL Maison Nour", ribPh: "007810000123456789012345",
    notesPh: "Ej. Preparamos muchas bodas en verano…",
    whatsappHint: "Opcional. Dejar vacío = mismo que el teléfono.",
    passwordHint: "8 caracteres mínimo.",
    chooseCity: "— Elegir —",
    errPhone: "Número demasiado corto.", errIce: "dígitos — un ICE marroquí tiene exactamente 15 dígitos.",
    errRib: "dígitos — un RIB marroquí tiene exactamente 24 dígitos.",
    errEmail: "Formato de correo no válido.", errPassword: "caracteres mínimo.",
    errWebsite: "URL no válida. Formato esperado: https://su-sitio.ma",
    errName: "Nombre demasiado corto.", errAddress: "Dirección demasiado corta.",
    status: "Su candidatura", documents: "Documentos", upload: "Subir",
    reuploadTitle: "Documentos por volver a subir", reuploadHint: "Estos son solo los documentos rechazados o que faltan. Vuelva a subirlos para continuar.",
    viewAll: "Ver todos los documentos", allUpToDate: "Todos sus documentos están al día. ¡Gracias!",
    stageLabels: {
      applied: "Recibida", documents_requested: "Documentos por completar", documents_verified: "Documentos validados",
      test_order: "Pedido de prueba", active: "Activa", rejected: "No aceptada",
    },
  },
  ar: {
    title: "كن شريكاً بائع ورود Fleurinelle",
    intro: "نعمل مع محلّات ورود قائمة في الدار البيضاء ومراكش والرباط وطنجة وأكادير، لديها قدرة توصيل خاصة. تصلكم الطلبات مدفوعة مسبقاً، والتسديد أسبوعي بالدرهم، ولا خدمة عملاء تتولّونها.",
    moroccoOnly: "🇲🇦 تعمل Fleurinelle فقط مع محلّات الورود القائمة فعلياً في المغرب.",
    latinNotice: "يجب إدخال جميع الحقول بالأحرف اللاتينية (a-z، 0-9).",
    shop: "اسم المحل", owner: "اسم المالك", email: "بريد مهني", password: "كلمة السرّ (8 أحرف على الأقل)",
    phone: "هاتف المالك", whatsapp: "واتساب", city: "المدينة", address: "العنوان الكامل للمحل",
    ice: "رقم ICE", years: "سنوات النشاط", zones: "المناطق التي تغطّونها",
    capacity: "الطلبات الممكنة في اليوم", website: "الموقع الإلكتروني", instagram: "إنستغرام",
    notes: "شيء آخر تودّون إخبارنا به", submit: "إرسال الطلب",
    submitting: "جارٍ الإرسال…", success: "تمّ إرسال الطلب",
    successHint: "حمّلوا وثائقكم من فضائكم لتسريع المراجعة.",
    section1: "1. محلّكم", section1Hint: "هذه المعلومات تظهر في ملفّكم الداخلي فقط. لا شيء يُنشر كما هو للمشتري.",
    section2: "2. بيانات الدخول", section2Hint: "ستدخلون بها إلى لوحة تحكّم بائع الورود.",
    section3: "3. التوصيل", section3Hint: "تحدّد Fleurinelle رسوم التوصيل. أنتم تختارون من يوصّل.",
    modeNetwork: "شبكة Fleurinelle", modeNetworkDesc: "يتولّى سائقونا كلّ شيء. تعود الرسوم إلى المنصّة.",
    modeSelf: "فريقي الخاص", modeSelfDesc: "يقوم سائقوكم بالتوصيل. تحتفظون بـ 100 % من رسوم التوصيل وتحدّدون أسعاركم الخاصّة حسب المنطقة.",
    section4: "3. المعطيات البنكية", section4Hint: "تسديد أسبوعي بالدرهم. يبقى RIB خاصاً ولا يظهر أبداً على الموقع.",
    bank: "البنك", holder: "صاحب الحساب", holderHint: "بالضبط كما يظهر على RIB.",
    rib: "RIB (24 رقماً)", ribHint: "بعد الإرسال، حمّلوا شهادة RIB من البنك. بدونها يبقى التفعيل معلّقاً.",
    docsSection: "4. الوثائق الإلزامية",
    docsSectionHint: "حمّلوا كلّ وثيقة بصيغة JPG أو PDF (8 ميغا كحدّ أقصى). يُراجعها فريقنا خلال 24 ساعة عمل.",
    docsAccept: "JPG أو PDF · 8 ميغا كحدّ أقصى",
    docUpload: "تحميل", docChange: "استبدال", recto: "الوجه الأمامي", verso: "الوجه الخلفي",
    docIce: "شهادة ICE (DGI)",
    docRc: "السجلّ التجاري (أقلّ من 3 أشهر)",
    docCin: "البطاقة الوطنية للمالك (الوجهان)",
    docRib: "شهادة RIB من البنك",
    section5: "5. كلمة لنا (اختياري)",
    notesHint: "الصور المفضّلة، التخصّصات، المواسم القويّة: كل ما يساعد فريقنا على معرفتكم.",
    consent: "قرأتُ ووافقتُ على", agreement: "عقد بائع الورود الشريك", and: "و", addendum: "ملحق معالجة البيانات", consentEnd: "، باسم محلّي.",
    submitHint: "أقلّ من 5 دقائق لتقديم الطلب. نردّ خلال 24 ساعة عمل.",
    iceHint: "15 رقماً، من شهادة ICE (DGI). للفوترة فقط.",
    addressHint: "الرقم، الشارع، الحي، الرمز البريدي.",
    pickupLabel: "نقطة الاستلام على الخريطة", pickupHint: "ابحثوا عن محلّكم في Google Maps أو ضعوا الدبّوس عند الباب مباشرة. سيتّبع السائقون الشركاء هذه النقطة.",
    photoLabel: "صورة الواجهة", photoHint: "صورة لمدخل محلّكم. سيراها السائقون عند الاستلام لتمييز الباب الصحيح.",
    photoUpload: "تحميل صورة", photoChange: "تغيير الصورة", photoBusy: "جارٍ التحميل…",
    zonesHint: "اختاروا كلّ المناطق التي تستعدّون لتوصيلها.",
    optional: "اختياري",
    yearsPh: "مثلاً 5", capacityPh: "مثلاً 8", websitePh: "https://maisonnour.ma", instagramPh: "@fleurinelle",
    shopPh: "Maison Nour", ownerPh: "الاسم الكامل", emailPh: "contact@maisonnour.ma", passwordPh: "••••••••",
    phonePh: "6 12 34 56 78", icePh: "مثلاً 002345678000012", addressPh: "12 rue Bir Anzarane, Racine, Casablanca 20100",
    bankPh: "Attijariwafa, BMCE, Banque Populaire…", holderPh: "مثلاً SARL Maison Nour", ribPh: "007810000123456789012345",
    notesPh: "مثلاً نُحضّر كثيراً من الأعراس في الصيف…",
    whatsappHint: "اختياري. اتركوه فارغاً = نفس رقم الهاتف.",
    passwordHint: "8 أحرف على الأقل.",
    chooseCity: "— اختر —",
    errPhone: "الرقم قصير جداً.", errIce: "رقماً — يتضمّن ICE المغربي 15 رقماً بالضبط.",
    errRib: "رقماً — يتضمّن RIB المغربي 24 رقماً بالضبط.",
    errEmail: "صيغة البريد غير صالحة.", errPassword: "أحرف على الأقل.",
    errWebsite: "رابط غير صالح. الصيغة المتوقّعة: https://votre-site.ma",
    errName: "الاسم قصير جداً.", errAddress: "العنوان قصير جداً.",
    status: "طلبكم", documents: "الوثائق", upload: "تحميل",
    reuploadTitle: "وثائق يجب إعادة تحميلها", reuploadHint: "هذه فقط الوثائق المرفوضة أو الناقصة. أعد تحميلها للمتابعة.",
    viewAll: "عرض كل الوثائق", allUpToDate: "كل وثائقكم محدّثة. شكراً لكم!",
    stageLabels: {
      applied: "تمّ الاستلام", documents_requested: "وثائق مطلوبة", documents_verified: "وثائق مُوثّقة",
      test_order: "طلب تجريبي", active: "مُفعَّل", rejected: "غير مقبول",
    },
  },
};

// Strips anything that isn't Latin letters, digits or common punctuation. Used on
// text fields to force Latin-only input (the platform's downstream systems — bank,
// invoicing, tax registry — only accept Latin characters).
const latinOnly = (s) => (s || "").replace(/[^A-Za-z0-9 .,'’\-@&/()#+\n]/g, "");

// A form field that uses CSS subgrid so labels, inputs and hints line up across
// every column of the parent grid — even when one hint is missing.
const Field = ({ label, hint, error, children, required, testid, span }) => (
  <label className={`grid grid-rows-subgrid gap-1 row-span-3 ${span === "full" ? "sm:col-span-2" : ""}`}
         data-testid={testid ? `${testid}-field` : undefined}>
    <span className="text-xs font-medium text-ink">
      {label}{required ? <span className="text-danger ms-1">*</span> : null}
    </span>
    <div className="w-full">{children}</div>
    <span className="text-[11px] text-muted min-h-[1rem]">
      {error ? <span className="text-danger">{error}</span> : hint || "\u00A0"}
    </span>
  </label>
);

// Two-column grid where children align across all three rows (label / input / hint).
const FieldGrid = ({ children }) => (
  <div className="grid sm:grid-cols-2 grid-rows-[auto_auto_auto] gap-x-4 gap-y-2">
    {children}
  </div>
);

const Section = ({ title, hint, children }) => (
  <div className="card p-5 space-y-4">
    <div>
      <h2 className="font-display text-lg text-ink">{title}</h2>
      {hint ? <p className="text-xs text-muted mt-0.5">{hint}</p> : null}
    </div>
    {children}
  </div>
);

export const FloristApply = ({ onAuthenticated }) => {
  const { locale = "en" } = useParams();
  const T = COPY[locale] || COPY.en;
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({
    shop_name: "", owner_name: "", email: "", password: "", phone: "", whatsapp: "", city_id: "",
    address: "", ice_number: "", years_in_business: "", zone_ids: [], capacity_per_day: "",
    website: "https://", instagram: "", notes: "", locale: locale === "ar" ? "ar" : locale === "fr" ? "fr" : locale === "es" ? "es" : "en",
    location_lat: null, location_lng: null, location_place_id: "", location_formatted_address: "",
    pickup_photo_url: "",
    bank_name: "", account_holder: "", rib: "", accepted_legal: false, has_own_delivery: false,
  });
  const [docs, setDocs] = useState({ ice_attestation: null, rc: null, id_card__recto: null, id_card__verso: null, rib: null });
  const [error, setError] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const pickDoc = (kind, file) => {
    setError("");
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setError("Fichier trop volumineux (8 Mo max)."); return; }
    const ct = (file.type || "").toLowerCase();
    if (!(ct === "image/jpeg" || ct === "image/jpg" || ct === "application/pdf")) {
      setError("Format non accepté. Chargez un JPG ou un PDF."); return;
    }
    setDocs((d) => ({ ...d, [kind]: file }));
  };

  const uploadPickupPhoto = async (file) => {
    if (!file) return;
    setError(""); setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/florist-applications/pickup-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, pickup_photo_url: data.url }));
    } catch (err) {
      setError(errText(err));
    } finally {
      setPhotoBusy(false);
    }
  };

  useEffect(() => {
    api.get("/florist-applications/meta", { params: { locale: "fr" } }).then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const dial = (v) => {
        const digits = (v || "").replace(/[\s-]/g, "").replace(/^\+?212/, "").replace(/^0+/, "");
        return digits ? `+212${digits}` : "";
      };
      const website = (form.website || "").trim();
      const { data } = await api.post("/florist-applications", {
        ...form,
        website: website === "https://" || website === "http://" ? "" : website,
        phone: dial(form.phone),
        whatsapp: dial(form.whatsapp || form.phone),
        ice_number: (form.ice_number || "").replace(/\D/g, ""),
        years_in_business: form.years_in_business === "" ? 0 : Number(form.years_in_business),
        capacity_per_day: form.capacity_per_day === "" ? 6 : Number(form.capacity_per_day),
      });
      localStorage.setItem("fl_token", data.token);
      // Upload the mandatory documents right after signup, using the JWT we just got.
      // Recto-verso docs use composite keys like "id_card__recto" -> doc_type + side.
      for (const [key, file] of Object.entries(docs)) {
        if (!file) continue;
        const [docType, docSide] = key.split("__");
        const fd = new FormData();
        fd.append("doc_type", docType);
        if (docSide) fd.append("side", docSide);
        fd.append("file", file);
        await api.post("/florist-applications/me/documents", fd,
                       { headers: { "Content-Type": "multipart/form-data" } });
      }
      trackPartnerSignup({
        audience: "florist",
        applicationId: data?.application_id || data?.id || "",
        city: form.city_id || "",
        mode: form.has_own_delivery ? "self" : "network",
      });
      // Hand the success message to the dashboard, then forward the new florist
      // straight into their applicant dashboard where it is shown as a banner.
      sessionStorage.setItem("fl_apply_success", data?.message || T.success);
      if (onAuthenticated) await onAuthenticated();
      window.location.href = "/florist";
    } catch (err) {
      setError(errText(err));
      setSubmitting(false);
    }
  };

  // Field-level validation shown inline as the shop-owner types. Only fires when a
  // field has content (so empty required fields don't scream red until submit).
  const fieldErrors = useMemo(() => {
    const e = {};
    const iceLen = (form.ice_number || "").replace(/\D/g, "").length;
    if (form.ice_number && iceLen !== 15) e.ice_number = `${iceLen}/15 ${T.errIce}`;
    const ribLen = (form.rib || "").replace(/\D/g, "").length;
    if (form.rib && ribLen !== 24) e.rib = `${ribLen}/24 ${T.errRib}`;
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = T.errEmail;
    if (form.password && form.password.length < 8) e.password = `${form.password.length}/8 ${T.errPassword}`;
    if (form.phone && (form.phone || "").replace(/\D/g, "").length < 9) e.phone = T.errPhone;
    if (form.whatsapp && (form.whatsapp || "").replace(/\D/g, "").length < 9) e.whatsapp = T.errPhone;
    const site = (form.website || "").trim();
    if (site && site !== "https://" && site !== "http://" && !/^https?:\/\/[^\s.]+\.[^\s]+/i.test(site)) {
      e.website = T.errWebsite;
    }
    if (form.shop_name && form.shop_name.trim().length < 2) e.shop_name = T.errName;
    if (form.owner_name && form.owner_name.trim().length < 2) e.owner_name = T.errName;
    if (form.bank_name && form.bank_name.trim().length < 2) e.bank_name = T.errName;
    if (form.account_holder && form.account_holder.trim().length < 2) e.account_holder = T.errName;
    if (form.address && form.address.trim().length < 5) e.address = T.errAddress;
    return e;
  }, [form, T]);

  // Red-border helper — keeps every input's error style consistent.
  const errClass = (k) => (fieldErrors[k] ? "!border-danger focus:!border-danger" : "");

  const iceDigits = (form.ice_number || "").replace(/\D/g, "").length;
  const canSubmit = form.accepted_legal && form.shop_name && form.owner_name && form.email && form.password.length >= 8
    && form.city_id && form.address && iceDigits === 15
    && form.location_lat != null && form.location_lng != null
    && form.pickup_photo_url
    && form.bank_name && form.account_holder && form.rib.replace(/\D/g, "").length === 24
    && docs.ice_attestation && docs.rc && docs.id_card__recto && docs.id_card__verso && docs.rib
    && Object.keys(fieldErrors).length === 0;

  return (
    <div>
      <PartnerLanding />
      <form onSubmit={submit} id="partner-apply-form" className="max-w-3xl mx-auto p-6 space-y-4 scroll-mt-8" data-testid="florist-apply-form">
      <h1 className="font-display text-3xl text-ink">{T.title}</h1>
      <p className="text-sm text-muted leading-relaxed">{T.intro}</p>
      <p className="text-sm text-ink" data-testid="apply-morocco-only">{T.moroccoOnly}</p>
      <p className="text-xs text-muted" data-testid="apply-latin-notice">{T.latinNotice}</p>

      <Section title={T.section1} hint={T.section1Hint}>
        <FieldGrid>
          <Field label={T.shop} required error={fieldErrors.shop_name}>
            <input data-testid="apply-shop-name" className={`field w-full ${errClass("shop_name")}`} placeholder={T.shopPh} value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: latinOnly(e.target.value) })} />
          </Field>
          <Field label={T.owner} required error={fieldErrors.owner_name}>
            <input data-testid="apply-owner-name" className={`field w-full ${errClass("owner_name")}`} placeholder={T.ownerPh} value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: latinOnly(e.target.value) })} />
          </Field>
          <Field label={T.city} required>
            <select data-testid="apply-city" className="field w-full" value={form.city_id} onChange={(e) => setForm({ ...form, city_id: e.target.value, zone_ids: [] })}>
              <option value="">{T.chooseCity}</option>
              {meta?.cities?.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>
          <Field label={T.ice} required error={fieldErrors.ice_number} hint={T.iceHint}>
            <input data-testid="apply-ice" inputMode="numeric" maxLength={15} className={`field w-full ${errClass("ice_number")}`}
                   placeholder={T.icePh}
                   value={form.ice_number}
                   onChange={(e) => setForm({ ...form, ice_number: e.target.value.replace(/\D/g, "").slice(0, 15) })} />
          </Field>
          <Field label={T.address} required error={fieldErrors.address} hint={T.addressHint} span="full">
            <textarea data-testid="apply-address" className={`field w-full h-20 ${errClass("address")}`} placeholder={T.addressPh} value={form.address} onChange={(e) => setForm({ ...form, address: latinOnly(e.target.value) })} />
          </Field>
          <Field label={T.pickupLabel} required hint={T.pickupHint} span="full">
            <div className="w-full">
              <LocationPicker
                value={form.location_lat ? { lat: form.location_lat, lng: form.location_lng, place_id: form.location_place_id, formatted_address: form.location_formatted_address } : null}
                cityHint={null}
                onChange={(v) => setForm((f) => ({ ...f, location_lat: v.lat, location_lng: v.lng, location_place_id: v.place_id, location_formatted_address: v.formatted_address }))}
              />
            </div>
          </Field>
          <Field label={T.photoLabel} required hint={T.photoHint} span="full">
            <div className="w-full flex flex-col sm:flex-row gap-3 items-start" data-testid="apply-shopfront-field">
              {form.pickup_photo_url ? (
                <img src={media(form.pickup_photo_url)} alt={T.photoLabel}
                     data-testid="apply-shopfront-preview"
                     className="w-40 h-28 object-cover border border-brass-light rounded-sm" />
              ) : (
                <div className="w-40 h-28 border border-dashed border-brass-light rounded-sm flex items-center justify-center text-muted">
                  <Upload size={20} />
                </div>
              )}
              <label className="btn-ghost text-xs py-2 px-3 cursor-pointer inline-flex items-center gap-2">
                <Upload size={13} />
                {photoBusy ? T.photoBusy : form.pickup_photo_url ? T.photoChange : T.photoUpload}
                <input type="file" accept="image/*" className="hidden" data-testid="apply-shopfront-upload"
                       onChange={(e) => e.target.files[0] && uploadPickupPhoto(e.target.files[0])} />
              </label>
            </div>
          </Field>
          <Field label={T.years} hint={T.optional}>
            <input data-testid="apply-years" type="number" min="0" max="80" className="field w-full" placeholder={T.yearsPh} value={form.years_in_business} onChange={(e) => setForm({ ...form, years_in_business: e.target.value })} />
          </Field>
          <Field label={T.capacity} hint={T.optional}>
            <input data-testid="apply-capacity" type="number" min="1" max="60" className="field w-full" placeholder={T.capacityPh} value={form.capacity_per_day} onChange={(e) => setForm({ ...form, capacity_per_day: e.target.value })} />
          </Field>
          <Field label={T.website} error={fieldErrors.website} hint={T.optional}>
            <input data-testid="apply-website" type="text" className={`field w-full ${errClass("website")}`} placeholder={T.websitePh} value={form.website} onChange={(e) => setForm({ ...form, website: latinOnly(e.target.value) })} />
          </Field>
          <Field label={T.instagram} hint={T.optional}>
            <input data-testid="apply-instagram" className="field w-full" placeholder={T.instagramPh} value={form.instagram} onChange={(e) => setForm({ ...form, instagram: latinOnly(e.target.value) })} />
          </Field>
        </FieldGrid>
      </Section>

      <Section title={T.section2} hint={T.section2Hint}>
        <FieldGrid>
          <Field label={T.email} required error={fieldErrors.email}>
            <input data-testid="apply-email" type="email" className={`field w-full ${errClass("email")}`} placeholder={T.emailPh} value={form.email} onChange={(e) => setForm({ ...form, email: latinOnly(e.target.value) })} />
          </Field>
          <Field label={T.password} required error={fieldErrors.password} hint={T.passwordHint}>
            <input data-testid="apply-password" type="password" className={`field w-full ${errClass("password")}`} placeholder={T.passwordPh} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Field>
          <Field label={T.phone} required error={fieldErrors.phone}>
            <div className="flex gap-2 w-full">
              <span className="field !w-[86px] flex items-center justify-center gap-1 text-sm whitespace-nowrap shrink-0">{MOROCCO.flag} {MOROCCO.dial}</span>
              <input data-testid="apply-phone" className={`field flex-1 ${errClass("phone")}`} type="tel" inputMode="tel" placeholder={T.phonePh} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </Field>
          <Field label={T.whatsapp} error={fieldErrors.whatsapp} hint={T.whatsappHint}>
            <div className="flex gap-2 w-full">
              <span className="field !w-[86px] flex items-center justify-center gap-1 text-sm whitespace-nowrap shrink-0">{MOROCCO.flag} {MOROCCO.dial}</span>
              <input data-testid="apply-whatsapp" className={`field flex-1 ${errClass("whatsapp")}`} type="tel" inputMode="tel" placeholder={T.phonePh} value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
            </div>
          </Field>
        </FieldGrid>
      </Section>

      <Section title={T.section4} hint={T.section4Hint}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={T.bank} required error={fieldErrors.bank_name}>
            <input data-testid="apply-bank-name" className={`field ${errClass("bank_name")}`} placeholder={T.bankPh} value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: latinOnly(e.target.value) })} />
          </Field>
          <Field label={T.holder} required error={fieldErrors.account_holder} hint={T.holderHint}>
            <input data-testid="apply-account-holder" className={`field ${errClass("account_holder")}`} placeholder={T.holderPh} value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: latinOnly(e.target.value) })} />
          </Field>
        </div>
        <Field label={T.rib} required error={fieldErrors.rib} hint={T.ribHint}>
          <input data-testid="apply-rib" inputMode="numeric" maxLength={24} className={`field ${errClass("rib")}`} placeholder={T.ribPh}
                 value={form.rib}
                 onChange={(e) => setForm({ ...form, rib: e.target.value.replace(/\D/g, "").slice(0, 24) })} />
        </Field>
      </Section>

      <Section title={T.docsSection} hint={T.docsSectionHint}>
        <div className="space-y-3" data-testid="apply-docs">
          {[
            { key: "ice_attestation", label: T.docIce },
            { key: "rc", label: T.docRc },
            { key: "id_card", label: T.docCin, rectoVerso: true },
            { key: "rib", label: T.docRib },
          ].map((f) => (
            <div key={f.key} className="flex flex-wrap items-center gap-3 border border-brass-light p-3">
              <div className="flex-1 min-w-[180px]">
                <p className="text-sm text-ink">{f.label} <span className="text-danger">*</span></p>
                {!f.rectoVerso && (docs[f.key]
                  ? <p className="text-[11px] text-success flex items-center gap-1"><CheckCircle2 size={12} /> {docs[f.key].name}</p>
                  : <p className="text-[11px] text-muted">{T.docsAccept}</p>)}
              </div>
              {f.rectoVerso ? (
                ["recto", "verso"].map((sideKey) => {
                  const stateKey = `${f.key}__${sideKey}`;
                  return (
                    <label key={sideKey} className="btn-ghost text-xs py-2 px-3 cursor-pointer inline-flex items-center gap-2">
                      {docs[stateKey] ? <CheckCircle2 size={13} className="text-success" /> : <Upload size={13} />}
                      {T[sideKey]}
                      <input type="file" accept="image/jpeg,application/pdf" className="hidden"
                             data-testid={`apply-doc-${stateKey}`}
                             onChange={(e) => pickDoc(stateKey, e.target.files[0])} />
                    </label>
                  );
                })
              ) : (
                <label className="btn-ghost text-xs py-2 px-3 cursor-pointer inline-flex items-center gap-2">
                  <Upload size={13} />
                  {docs[f.key] ? T.docChange : T.docUpload}
                  <input type="file" accept="image/jpeg,application/pdf" className="hidden"
                         data-testid={`apply-doc-${f.key}`}
                         onChange={(e) => pickDoc(f.key, e.target.files[0])} />
                </label>
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section title={T.section5}>
        <Field label={T.notes} hint={T.notesHint}>
          <textarea data-testid="apply-notes" className="field h-20" placeholder={T.notesPh} value={form.notes} onChange={(e) => setForm({ ...form, notes: latinOnly(e.target.value) })} />
        </Field>
      </Section>

      {error ? <p className="text-danger text-sm card p-3" data-testid="apply-error">{error}</p> : null}
      <label className="flex items-start gap-3 text-xs text-charcoal cursor-pointer" data-testid="apply-consent-row">
        <input type="checkbox" data-testid="apply-consent" className="mt-0.5 shrink-0"
               checked={form.accepted_legal} onChange={(e) => setForm({ ...form, accepted_legal: e.target.checked })} />
        <span>
          {T.consent}{" "}
          <a href={`/${locale}/florist-agreement`} target="_blank" rel="noreferrer" className="text-brass underline">{T.agreement}</a>{" "}
          {T.and}{" "}
          <a href={`/${locale}/florist-privacy`} target="_blank" rel="noreferrer" className="text-brass underline">{T.addendum}</a>{T.consentEnd}
        </span>
      </label>
      <button data-testid="apply-submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={!canSubmit || submitting}>
        {submitting ? <><Loader2 size={16} className="animate-spin" /> {T.submitting}</> : T.submit}
      </button>
      <p className="text-[11px] text-muted text-center">{T.submitHint}</p>
      </form>
      {submitting ? (
        <div className="fixed inset-0 z-50 bg-ink/75 backdrop-blur-sm flex flex-col items-center justify-center gap-4"
             data-testid="apply-submitting-overlay">
          <Loader2 size={44} className="animate-spin text-brass" />
          <p className="text-plaster text-sm font-display">{T.submitting}</p>
        </div>
      ) : null}
    </div>
  );
};

export const ApplicationStatus = ({ onSignOut }) => {
  const { locale = "en" } = useParams();
  const T = COPY[locale] || COPY.en;
  const [app, setApp] = useState(null);
  const [error, setError] = useState("");
  const [welcome, setWelcome] = useState("");
  // Arriving from a "document rejected" e-mail (…/florist?reupload=1) shows only the
  // documents that still need action, hiding the ones already approved.
  const reuploadMode = new URLSearchParams(window.location.search).get("reupload") === "1";
  const [showAll, setShowAll] = useState(false);
  const REQUIRED_KEYS = ["ice_attestation", "rc", "id_card", "rib"];

  const currentLocale = useRef(locale);
  currentLocale.current = locale;
  const load = useCallback((signal) => api.get("/florist-applications/me", { params: { locale }, signal })
    .then((r) => { if (!signal?.aborted && currentLocale.current === locale) setApp(r.data); })
    .catch((e) => { if (!signal?.aborted && currentLocale.current === locale) setError(errText(e)); }), [locale]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    const msg = sessionStorage.getItem("fl_apply_success");
    if (msg) { setWelcome(msg); sessionStorage.removeItem("fl_apply_success"); }
    return () => controller.abort();
  }, [load]);

  const upload = async (docType, file, side = "") => {
    const fd = new FormData();
    fd.append("doc_type", docType);
    if (side) fd.append("side", side);
    fd.append("file", file);
    try {
      await api.post("/florist-applications/me/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      load();
    } catch (e) {
      setError(errText(e));
    }
  };

  if (!app) return <div className="p-8 text-muted text-sm">{error || "…"}</div>;

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6" data-testid="application-status">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-ink">{T.status}</h1>
          <p className="text-sm text-muted">{app.shop_name} · {app.city_name}</p>
        </div>
        <button className="btn-ghost text-[11px]" data-testid="applicant-logout" onClick={onSignOut}>Déconnexion</button>
      </div>

      {welcome ? (
        <div className="card p-4 border-success bg-success/5 flex items-start gap-3" data-testid="apply-success-banner">
          <CheckCircle2 className="text-success shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="text-sm text-ink font-medium">{welcome}</p>
            <p className="text-xs text-muted">{T.successHint}</p>
          </div>
          <button className="text-muted text-sm leading-none" data-testid="apply-success-dismiss"
                  onClick={() => setWelcome("")}>✕</button>
        </div>
      ) : null}

      <ol className="flex flex-wrap gap-2 text-xs" data-testid="application-stages">
        {app.stages.filter((s) => s !== "rejected").map((s, i) => (
          <li key={s} className={`px-3 py-1.5 border ${i <= app.stage_index ? "border-brass text-ink bg-plaster-deep" : "border-brass-light text-muted"}`}>
            {T.stageLabels[s]}
          </li>
        ))}
      </ol>
      <p className="text-sm text-charcoal card p-4" data-testid="application-message">{app.status_message}</p>
      {app.review_note ? <p className="text-sm text-warning flex gap-2"><AlertTriangle size={15} />{app.review_note}</p> : null}

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-xl text-ink">{reuploadMode && !showAll ? T.reuploadTitle : T.documents}</h2>
          {reuploadMode ? (
            <button className="text-xs underline text-muted" data-testid="doc-view-toggle"
                    onClick={() => setShowAll((v) => !v)}>
              {showAll ? T.reuploadTitle : T.viewAll}
            </button>
          ) : null}
        </div>
        {reuploadMode && !showAll ? (
          <p className="text-xs text-muted" data-testid="reupload-hint">{T.reuploadHint}</p>
        ) : null}
        {(() => {
          const findDoc = (key, side) => (app.documents || []).find((x) => x.doc_type === key && (x.side || "") === (side || ""));
          const needsAction = (d) => {
            if (d.recto_verso) {
              return ["recto", "verso"].some((s) => {
                const up = findDoc(d.key, s);
                return up ? up.status === "rejected" : true;
              });
            }
            const up = findDoc(d.key, "");
            if (up) return up.status === "rejected";
            return REQUIRED_KEYS.includes(d.key);
          };
          const visibleDocs = (reuploadMode && !showAll)
            ? app.required_documents.filter(needsAction)
            : app.required_documents;
          if (visibleDocs.length === 0) {
            return <p className="text-sm text-success card p-4" data-testid="docs-all-up-to-date">{T.allUpToDate}</p>;
          }
          const statusLine = (up) => (
            up ? (
              <p className={`text-xs ${up.status === "rejected" ? "text-danger" : up.status === "approved" ? "text-success" : "text-muted"}`}>
                {up.status}{up.note ? ` — ${up.note}` : ""}
              </p>
            ) : <p className="text-xs text-muted">—</p>
          );
          return visibleDocs.map((d) => {
            if (d.recto_verso) {
              return (
                <div key={d.key} className="card p-4 space-y-3 text-sm" data-testid={`doc-row-${d.key}`}>
                  <p className="text-ink">{d.label}</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {["recto", "verso"].map((s) => {
                      const up = findDoc(d.key, s);
                      return (
                        <div key={s} className="flex-1 flex items-center gap-3 border border-brass-light p-2">
                          <div className="flex-1">
                            <p className="text-xs text-ink">{T[s]}</p>
                            {statusLine(up)}
                          </div>
                          {up ? <a href={media(up.url)} target="_blank" rel="noreferrer" className="text-xs underline text-muted">Voir</a> : null}
                          <label className="btn-ghost text-[11px] py-1.5 px-3 cursor-pointer flex items-center gap-2">
                            <Upload size={13} />
                            {T.upload}
                            <input type="file" accept="image/*,application/pdf" className="hidden" data-testid={`doc-upload-${d.key}-${s}`} onChange={(e) => e.target.files[0] && upload(d.key, e.target.files[0], s)} />
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }
            const uploaded = findDoc(d.key, "");
            return (
              <div key={d.key} className="card p-4 flex items-center gap-3 text-sm" data-testid={`doc-row-${d.key}`}>
                <div className="flex-1">
                  <p className="text-ink">{d.label}</p>
                  {statusLine(uploaded)}
                </div>
                {uploaded ? (
                  <a href={media(uploaded.url)} target="_blank" rel="noreferrer" className="text-xs underline text-muted">Voir</a>
                ) : null}
                <label className="btn-ghost text-[11px] py-1.5 px-3 cursor-pointer flex items-center gap-2">
                  <Upload size={13} />
                  {T.upload}
                  <input type="file" accept="image/*,application/pdf" className="hidden" data-testid={`doc-upload-${d.key}`} onChange={(e) => e.target.files[0] && upload(d.key, e.target.files[0])} />
                </label>
              </div>
            );
          });
        })()}
      </div>
      {error ? <p className="text-danger text-sm" data-testid="application-error">{error}</p> : null}
    </div>
  );
};
